import * as React from "react";
import { FileUp } from "lucide-react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";

export interface BrokerCsvFileUploadFieldProps {
  id: string;
  label: string;
  description?: string | null;
  accept: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  required?: boolean;
  chooseLabel?: string;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function BrokerCsvFileUploadField({
  id,
  label,
  description,
  accept,
  file,
  onFileChange,
  required = false,
  chooseLabel = "Choose file",
  className,
}: BrokerCsvFileUploadFieldProps): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const labelId = `${id}-label`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileChange(event.target.files?.[0] ?? null);
  };

  return (
    <Field className={cn("relative gap-2", className)} data-broker-csv-file-upload="">
      <FieldLabel id={labelId} className={BROKER_ALERT_TITLE_CLASS}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </FieldLabel>
      {description ? (
        <FieldDescription className={BROKER_ALERT_DESC_CLASS}>{description}</FieldDescription>
      ) : null}
      {/*
        Keep the native input out of hit-testing. Field uses `*:w-full`, which can
        stretch an sr-only file input into a full-width invisible click target.
        Only the Choose file button should open the picker.
      */}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        tabIndex={-1}
        aria-labelledby={labelId}
        className="pointer-events-none absolute top-0 left-0 h-px w-px overflow-hidden opacity-0"
        onChange={handleChange}
      />
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="outline"
          data-font="primary"
          className="h-8 w-fit shrink-0 px-5 text-[0.875rem] normal-case tracking-normal"
          onClick={() => inputRef.current?.click()}
        >
          <FileUp
            className="size-4 text-[color:var(--brand-tropical-surf)]"
            aria-hidden
          />
          <span className="!normal-case font-normal tracking-normal text-gray-500">
            {chooseLabel}
          </span>
        </Button>
        {file ? (
          <p className="min-w-0 truncate font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
            {file.name}
            <span className="text-gray-500"> · {formatFileSize(file.size)}</span>
          </p>
        ) : (
          <p className="font-[family-name:var(--font-primary)] text-sm text-gray-500">
            No file selected
          </p>
        )}
      </div>
    </Field>
  );
}
